import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Nav, Alert } from "react-bootstrap";
import { FaUsers, FaListUl, FaCar, FaPhone, FaEnvelope } from "react-icons/fa";
import { settingsAPI } from "../../api/apiClient";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/css/Settings.css";
import { useTranslation } from 'react-i18next';

export default function GlobalDashboard() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("logo");
  const [settings, setSettings] = useState({
    logo: "",
    favicon: "",
    footer_col1_title: "",
    footer_col2_title: "",
    footer_col3_title: "",
    footer_col4_title: "",
    footer_address: "",
    footer_email: "",
    footer_phone: "",
    top_bar_email: "",
    top_bar_phone: "",
    send_email_from: "",
    receive_email_to: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [faviconPreview, setFaviconPreview] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      if (response.success) {
        const settingsData = response.data.data && response.data.data.length > 0 
          ? response.data.data[0] 
          : response.data;
        
        setSettings(prevSettings => ({
          ...prevSettings,
          ...settingsData
        }));
        
        setLogoPreview(settingsData.logo || "");
        setFaviconPreview(settingsData.favicon || "");
      } else {
        setError(response.message || t('failed_to_fetch_settings'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_settings'));
      console.error('Error fetching settings:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e, tab) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const fd = new FormData();

      if (tab === 'logo') {
        if (logoFile) fd.append('logo', logoFile);
      } else if (tab === 'favicon') {
        if (faviconFile) fd.append('favicon', faviconFile);
      } else if (tab === 'header') {
        fd.append('top_bar_email', settings.top_bar_email);
        fd.append('top_bar_phone', settings.top_bar_phone);
      } else if (tab === 'footer') {
        fd.append('footer_col1_title', settings.footer_col1_title);
        fd.append('footer_col2_title', settings.footer_col2_title);
        fd.append('footer_col3_title', settings.footer_col3_title);
        fd.append('footer_col4_title', settings.footer_col4_title);
        fd.append('footer_address', settings.footer_address);
        fd.append('footer_email', settings.footer_email);
        fd.append('footer_phone', settings.footer_phone);
      } else if (tab === 'email') {
        fd.append('send_email_from', settings.send_email_from);
        fd.append('receive_email_to', settings.receive_email_to);
      }

      const response = await settingsAPI.update(fd);
      if (response.success) {
        setSuccess(t('settings_updated'));
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('settings_updated_success'),
          timer: 2000,
          showConfirmButton: false
        });
        fetchSettings(); // Refresh data
      } else {
        setError(response.message || t('failed_to_update_settings'));
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_update_settings'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      setError(t('failed_to_update_settings'));
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_update_settings'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error updating settings:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-header">
        <h4 className="mb-0">{t("admin_area")}</h4>
      </div>
      <div className="py-4">
        <h5 className="mb-4 dashboard-title">{t("settings_section")}</h5>
        <Nav
          variant="tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4"
        >
          <Nav.Item>
            <Nav.Link eventKey="logo">{t("Logo")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="favicon">{t("favicon")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="header">{t("top_bar")}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="footer">{t("footer")}</Nav.Link>
          </Nav.Item>
          {/* <Nav.Item>
            <Nav.Link eventKey="email">{t("Email")}</Nav.Link>
          </Nav.Item> */}
        </Nav>

        {/* {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>} */}

        {activeTab === "logo" && (
          <Card className="dashboard-card mb-4">
            <Card.Body>
              <div className="text-center mb-3">
                <p>{t("existing_photo")}</p>
                <img src={logoPreview} alt="Existing Logo" style={{ maxWidth: "150px" }} />
              </div>
              <Form onSubmit={(e) => handleSubmit(e, 'logo')}>
                <Form.Group controlId="formLogoFile" className="mb-3 text-center">
                  <Form.Label>{t("new_photo")}</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => handleFileChange(e, setLogoFile, setLogoPreview)}
                    accept="image/*"
                  />
                </Form.Group>
                <div className="text-center">
                  <Button variant="primary" className="custom-btn" type="submit" disabled={loading}>
                    {loading ? "Uploading..." : "Logo Aktualisieren"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}

        {activeTab === "favicon" && (
          <Card className="dashboard-card mb-4">
            <Card.Body>
              <div className="text-center mb-3">
                <p>{t("existing_favicon")}</p>
                <img src={faviconPreview} alt="Existing Favicon" style={{ maxWidth: "50px" }} />
              </div>
              <Form onSubmit={(e) => handleSubmit(e, 'favicon')}>
                <Form.Group controlId="formFaviconFile" className="mb-3 text-center">
                  <Form.Label>{t("New favicon")}</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => handleFileChange(e, setFaviconFile, setFaviconPreview)}
                    accept="image/*"
                  />
                </Form.Group>
                <div className="text-center">
                  <Button variant="primary" className="custom-btn" type="submit" disabled={loading}>
                    {loading ? "Uploading..." : "Favicon Aktualisieren"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}

        {activeTab === "header" && (
          <Card className="dashboard-card mb-4">
            <Card.Body>
              <Form onSubmit={(e) => handleSubmit(e, 'header')}>
                <Form.Group className="mb-3" controlId="topBarEmail">
                  <Form.Label>{t("top_bar_email")}</Form.Label>
                  <Form.Control
                    type="email"
                    name="top_bar_email"
                    value={settings.top_bar_email || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="topBarPhone">
                  <Form.Label>{t("top_bar_phone")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="top_bar_phone"
                    value={settings.top_bar_phone || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Button variant="primary" className="custom-btn" type="submit" disabled={loading}>
                  {loading ? "Updating..." : "E-Mail Aktualisieren"}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        )}

        {activeTab === "footer" && (
          <Card className="dashboard-card mb-4">
            <Card.Body>
              <Form onSubmit={(e) => handleSubmit(e, 'footer')}>
                <Form.Group className="mb-3" controlId="column1Title">
                  <Form.Label>{t("column1_title")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="footer_col1_title"
                    value={settings.footer_col1_title || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="column2Title">
                  <Form.Label>{t("column2_title")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="footer_col2_title"
                    value={settings.footer_col2_title || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="column3Title">
                  <Form.Label>{t("column3_title")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="footer_col3_title"
                    value={settings.footer_col3_title || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="column4Title">
                  <Form.Label>{t("column4_title")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="footer_col4_title"
                    value={settings.footer_col4_title || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="footerAddress">
                  <Form.Label>{t("footer_address")}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="footer_address"
                    value={settings.footer_address || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="footerEmail">
                  <Form.Label>{t("footer_email")}</Form.Label>
                  <Form.Control
                    type="email"
                    name="footer_email"
                    value={settings.footer_email || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="footerPhone">
                  <Form.Label>{t("footer_phone")}</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="footer_phone"
                    value={settings.footer_phone || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Button variant="primary" className="custom-btn" type="submit" disabled={loading}>
                  {loading ? "Saving..." : "Speichern"}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        )}

        {activeTab === "email" && (
          <Card className="dashboard-card mb-4">
            <Card.Body>
              <Form onSubmit={(e) => handleSubmit(e, 'email')}>
                <Form.Group className="mb-3" controlId="sendEmailFrom">
                  <Form.Label>{("send_email_from")}</Form.Label>
                  <Form.Control
                    type="email"
                    name="send_email_from"
                    value={settings.send_email_from || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="receiveEmailTo">
                  <Form.Label>{t('receive_email_to')}</Form.Label>
                  <Form.Control
                    type="email"
                    name="receive_email_to"
                    value={settings.receive_email_to || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Button variant="primary" className="custom-btn" type="submit" disabled={loading}>
                  {loading ? "Updating..." : "E-Mail Aktualisieren"}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
}